import React from "react";

import { DateTime } from "luxon";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTruck, faHashtag } from "@fortawesome/free-solid-svg-icons";
import { ClockIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";

import Modal from "../../layouts/Modal";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import PriceFormat from "../../utils/priceFormat";
import formatDateTime from "../../utils/formatDateTime";
import { IOptimiseResult, IOptimiseTrip } from "../../models/TripOptimisation";
import { formatMinutesIntoHours } from "../../utils/formatMinutesIntoHours";
import PrintOptimisedRoute from "./PrintOptimisedRoute";

export interface UnroutableLeadInfo {
  leadId: string;
  vrm: string;
  makeModel?: string;
  postcode: string;
}

interface OptimiseTripsModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  result?: IOptimiseResult;
  tripColours: Record<string, string>;
  /** Current per-trip start time (HH:mm), keyed by trip id. */
  startTimes: Record<string, string>;
  onStartTimeChange: (tripId: string, value: string) => void;
  onConfirm: () => void;
  isSaving: boolean;
  /** True while a start-time change is re-running the optimise preview. */
  isOptimising: boolean;
  /** Details for the unroutable leads, with a per-lead geocode action. */
  unroutableLeads?: UnroutableLeadInfo[];
  onGeocode?: (leadId: string) => void;
  geocodingLeadId?: string;
  selectedDay?: string;
}

// Verbose form for headline figures, e.g. "5 hours 24 minutes".
const formatVerbose = (seconds: number): string =>
  formatMinutesIntoHours(Math.round(seconds / 60));

// "1h 05m" / "25 min" from a number of seconds.
const formatSeconds = (seconds: number): string => {
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins} min`;
  const hours = Math.floor(mins / 60);
  const remainder = mins % 60;
  return remainder > 0
    ? `${hours}h ${remainder.toString().padStart(2, "0")}m`
    : `${hours}h`;
};

const formatClock = (iso?: string | null): string =>
  iso ? formatDateTime(iso, DateTime.TIME_24_SIMPLE) : "";

// A break row slotted into the stop list, with its clock time.
function BreakRow({ atISO }: { atISO?: string | null }) {
  return (
    <li className="flex items-center gap-x-2 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-700">
      <ClockIcon className="h-4 w-4 flex-none" />
      <span>45-minute break{atISO ? ` · ${formatClock(atISO)}` : ""}</span>
    </li>
  );
}

// A depart / return marker slotted into the stop list, with its clock time.
function EndpointRow({ label, timeISO }: { label: string; timeISO?: string | null }) {
  return (
    <li className="flex items-center justify-between bg-gray-50 px-3 py-1.5 text-xs font-medium text-gray-600">
      <span>{label}</span>
      {timeISO && <span className="tabular-nums text-gray-700">{formatClock(timeISO)}</span>}
    </li>
  );
}

export default function OptimiseTripsModal({
  open,
  setOpen,
  result,
  tripColours,
  startTimes,
  onStartTimeChange,
  onConfirm,
  isSaving,
  isOptimising,
  unroutableLeads,
  onGeocode,
  geocodingLeadId,
  selectedDay,
}: OptimiseTripsModalProps) {
  const totalStops = result?.trips.reduce((sum, t) => sum + t.stops.length, 0) ?? 0;
  const savings = result?.savings;

  // Trips grouped by truck so a truck running multiple trips shows them together,
  // ordered by departure (the backend chains a truck's trips through the day).
  const truckGroups = (() => {
    const groups: { truckName: string; trips: IOptimiseTrip[] }[] = [];
    (result?.trips ?? [])
      .filter((trip) => trip.stops.length > 0)
      .forEach((trip) => {
        const name = trip.truckName ?? "Trip";
        let group = groups.find((g) => g.truckName === name);
        if (!group) {
          group = { truckName: name, trips: [] };
          groups.push(group);
        }
        group.trips.push(trip);
      });
    groups.forEach((group) =>
      group.trips.sort((a, b) => (a.departIso ?? "").localeCompare(b.departIso ?? ""))
    );
    return groups;
  })();

  // The truck's total driving for the day (summed over its trips here), shown as
  // a chip in each card header; red when it breaks the 9h limit.
  const renderTrip = (trip: IOptimiseTrip, label: string, dayDrivingSeconds: number) => (
    <div key={trip.tripId} className="rounded-md border border-gray-200 bg-white">
      <div className="flex items-center justify-between border-b border-gray-100 px-3 py-2">
        <div className="flex items-center gap-x-2">
          <span
            className="h-3 w-3 rounded-full"
            style={{ backgroundColor: tripColours[trip.tripId] ?? "#6b7280" }}
          />
          <span className="text-sm font-semibold text-gray-800">
            {label}
          </span>
          <span
            title="Estimated daily driving (9h limit)"
            className={
              "inline-flex items-center gap-x-1 rounded-full px-2 py-0.5 text-xs font-medium " +
              (trip.overDailyDriving
                ? "bg-red-50 text-red-600"
                : "bg-gray-100 text-gray-600")
            }
          >
            <ClockIcon className="h-3.5 w-3.5" />
            {formatVerbose(dayDrivingSeconds)}
          </span>
        </div>
        <span
          className={
            trip.overCapacity ? "text-xs font-medium text-red-600" : "text-xs text-gray-500"
          }
        >
          {trip.stops.length} / {trip.capacity}
        </span>
      </div>

      {/* Trip summary: driving/loading, plus a compact start-time picker inline. */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-gray-100 bg-gray-50/60 px-3 py-2 text-xs">
        <label className="flex items-center gap-x-1 text-gray-600">
          <ClockIcon className="h-3.5 w-3.5 text-gray-400" />
          Start
          <input
            type="time"
            step={900}
            value={startTimes[trip.tripId] ?? trip.startTime}
            onChange={(e) => onStartTimeChange(trip.tripId, e.target.value)}
            className="ml-1 h-6 border-gray-400 py-0 text-xs"
          />
        </label>
        <span className="text-gray-600">Driving {formatVerbose(trip.drivingSeconds)}</span>
        <span className="text-gray-600">Loading {formatVerbose(trip.loadingSeconds)}</span>
        {trip.departIso && (
          <span className="flex items-center gap-x-1 font-medium text-gray-700">
            <ClockIcon className="h-3.5 w-3.5 text-gray-400" />
            {formatClock(trip.departIso)} → {formatClock(trip.returnIso)}
          </span>
        )}
      </div>

      <ol className="divide-y divide-gray-50">
        {trip.unloadBeforeIso && (
          <EndpointRow label="Unload at site (30 min)" timeISO={trip.unloadBeforeIso} />
        )}
        <EndpointRow label="Depart site" timeISO={trip.departIso} />
        {trip.stops.map((stop) => (
          <React.Fragment key={stop.leadId}>
            {stop.breakBeforeIso && <BreakRow atISO={stop.breakBeforeIso} />}
            <li className="px-3 py-1.5 text-sm">
              <div className="flex items-center justify-between">
                <div className="flex min-w-0 items-center gap-x-2">
                  <span className="flex h-5 w-5 flex-none items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-700">
                    {stop.order + 1}
                  </span>
                  <span className="font-medium text-gray-800">{stop.vrm ?? "No VRM"}</span>
                  {[stop.make, stop.model].filter(Boolean).join(" ") && (
                    <span className="truncate text-gray-600">
                      {[stop.make, stop.model].filter(Boolean).join(" ")}
                    </span>
                  )}
                  <span className="truncate text-gray-500">{stop.postcode}</span>
                  {stop.isVan && (
                    <FontAwesomeIcon icon={faTruck} title="Van" className="text-primary-600" />
                  )}
                  {stop.is4x4 && (
                    <FontAwesomeIcon icon={faHashtag} title="4x4" className="text-primary-600" />
                  )}
                </div>
                <span className="flex flex-none items-center gap-x-2 text-xs">
                  <span className="text-gray-400">
                    {stop.legDistanceKm != null
                      ? `${stop.legDistanceKm.toFixed(1)} km${
                          stop.legDrivingSeconds != null
                            ? ` · ${formatSeconds(stop.legDrivingSeconds)}`
                            : ""
                        }`
                      : `${stop.distanceKm.toFixed(1)} km`}
                  </span>
                  {stop.arriveIso && (
                    <span className="font-semibold tabular-nums text-gray-700">
                      {formatClock(stop.arriveIso)}
                    </span>
                  )}
                </span>
              </div>
              {[stop.driverNotes, stop.vehicleNotes]
                .filter((note) => note && note.trim().length > 0)
                .join(" — ") && (
                <div className="pl-7 pt-0.5 text-xs italic text-gray-500">
                  {[stop.driverNotes, stop.vehicleNotes]
                    .filter((note) => note && note.trim().length > 0)
                    .join(" — ")}
                </div>
              )}
            </li>
          </React.Fragment>
        ))}
        {trip.breakBeforeReturnIso && <BreakRow atISO={trip.breakBeforeReturnIso} />}
        <EndpointRow label="Return to site" timeISO={trip.returnIso} />
      </ol>

      {(trip.overCapacity ||
        trip.overDailyDriving ||
        trip.vanFirstUnavoidable ||
        trip.fourByFourLastUnavoidable) && (
        <div className="flex flex-col gap-y-1 border-t border-gray-100 px-3 py-2">
          {trip.overCapacity && (
            <span className="flex items-center gap-x-1 text-xs text-red-600">
              <ExclamationTriangleIcon className="h-4 w-4" />
              Over truck capacity
            </span>
          )}
          {trip.overDailyDriving && (
            <span className="flex items-center gap-x-1 text-xs text-red-600">
              <ExclamationTriangleIcon className="h-4 w-4" />
              Over the 9h daily driving limit
            </span>
          )}
          {trip.vanFirstUnavoidable && (
            <span className="flex items-center gap-x-1 text-xs text-amber-600">
              <ExclamationTriangleIcon className="h-4 w-4" />
              Could not avoid starting on a van
            </span>
          )}
          {trip.fourByFourLastUnavoidable && (
            <span className="flex items-center gap-x-1 text-xs text-amber-600">
              <ExclamationTriangleIcon className="h-4 w-4" />
              Could not avoid finishing on a 4x4
            </span>
          )}
        </div>
      )}
    </div>
  );

  return (
    <Modal open={open} setOpen={setOpen} width="max-w-3xl">
      <div className="flex flex-col gap-y-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">Optimise trips for the day</h2>
          <p className="text-sm text-gray-500">
            Starts at the northernmost collection (unless the east/west extreme is 10+ miles
            further out) then works the nearest stops by road back towards site, never starting on
            a van or finishing on a 4x4. Set each trip's start time to see when the driver needs to
            break, then review the proposed order before saving.
          </p>
        </div>

        {savings && totalStops > 0 && (
          <div className="flex items-center justify-between rounded-md border border-primary-200 bg-primary-50 px-3 py-2 text-sm">
            <span className="text-gray-600">
              Driving distance {savings.currentKm.toFixed(1)} km → {savings.optimisedKm.toFixed(1)} km
            </span>
            <span
              className={
                savings.savingsKm >= 0.05
                  ? "font-semibold text-green-600"
                  : savings.savingsKm <= -0.05
                  ? "font-semibold text-red-600"
                  : "font-semibold text-gray-500"
              }
            >
              {savings.savingsKm >= 0
                ? `Saves ${savings.savingsKm.toFixed(1)} km · ${PriceFormat(savings.fuelCost)}`
                : `+${Math.abs(savings.savingsKm).toFixed(1)} km · +${PriceFormat(Math.abs(savings.fuelCost))}`}
              {savings.currentKm > 0 &&
                ` (${((savings.savingsKm / savings.currentKm) * 100).toFixed(0)}%)`}
            </span>
          </div>
        )}

        {!result || totalStops === 0 ? (
          <div className="rounded-md border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
            No routable collections to optimise for this day.
          </div>
        ) : (
          <div className="flex max-h-[55vh] flex-col gap-y-3 overflow-y-auto pr-1">
            {truckGroups.map((group) => {
              const multiTrip = group.trips.length > 1;
              const groupStops = group.trips.reduce((sum, t) => sum + t.stops.length, 0);
              const groupDriving = group.trips.reduce((sum, t) => sum + t.drivingSeconds, 0);

              // A single-trip truck doesn't need a group wrapper — show its card
              // headed by the truck name as before.
              if (!multiTrip) {
                return (
                  <React.Fragment key={group.trips[0].tripId}>
                    {renderTrip(group.trips[0], group.truckName, groupDriving)}
                  </React.Fragment>
                );
              }

              return (
                <div
                  key={group.truckName}
                  className="rounded-md border border-gray-300 bg-gray-50"
                >
                  <div className="flex items-center justify-between px-3 py-2">
                    <span className="flex items-center gap-x-2 text-sm font-semibold text-gray-800">
                      <FontAwesomeIcon icon={faTruck} className="text-gray-400" />
                      {group.truckName}
                    </span>
                    <span className="text-xs text-gray-500">
                      {group.trips.length} trips · {groupStops} stops
                    </span>
                  </div>
                  <div className="flex flex-col gap-y-2 px-2 pb-2">
                    {group.trips.map((trip, index) =>
                      renderTrip(trip, `Trip ${index + 1}`, groupDriving)
                    )}
                  </div>
                </div>
              );
            })}

            {result.unroutableLeadIds.length > 0 && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                <div className="flex items-center gap-x-1 font-medium">
                  <ExclamationTriangleIcon className="h-4 w-4 flex-none" />
                  {result.unroutableLeadIds.length} collection
                  {result.unroutableLeadIds.length === 1 ? "" : "s"} without a mapped location —
                  kept on {result.unroutableLeadIds.length === 1 ? "its" : "their"} current trip in
                  the existing order.
                </div>
                {unroutableLeads && unroutableLeads.length > 0 && (
                  <ul className="mt-2 flex flex-col gap-y-1">
                    {unroutableLeads.map((lead) => (
                      <li key={lead.leadId} className="flex items-center justify-between gap-x-2">
                        <span>
                          <span className="font-medium">{lead.vrm}</span>
                          {lead.makeModel && <span className="ml-2">{lead.makeModel}</span>}
                          {lead.postcode && <span className="ml-2">{lead.postcode}</span>}
                        </span>
                        {onGeocode && lead.postcode && (
                          <ButtonInput
                            label="Geocode from postcode"
                            isSubmit={false}
                            classes=""
                            size="sm"
                            onClick={() => onGeocode(lead.leadId)}
                            isLoading={geocodingLeadId === lead.leadId}
                          />
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-between gap-x-3">
          <PrintOptimisedRoute
            result={result}
            selectedDay={selectedDay}
            tripColours={tripColours}
            instanceId="modal"
          />
          <div className="flex justify-end gap-x-3">
            <ButtonInput isSubmit={false} classes="" onClick={() => setOpen(false)}>
              <span>Cancel</span>
            </ButtonInput>
            <ButtonInput
              label="Apply order"
              isSubmit={false}
              classes=""
              onClick={onConfirm}
              isLoading={isSaving || isOptimising}
              disabled={!result || totalStops === 0}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
}
